import { Action } from 'redux';
import { AppState } from 'src';
import { Dispatch } from 'react-redux';
import { PageViewModel, AppDispatcher } from '@models/common.models';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType } from '@common/components';
import * as React from 'react';
import { ToastOptions } from '@common/components/toast/toast';
import { InvoiceViewModel } from '@models/Invoice';
import { invoiceService } from '@services/InvoiceService';
import { ProfileViewModel } from '@models/Auth';
import { InvoiceSearchViewModel } from '../_models/Invoice';
import { swoService } from '@services/ServiceWorkOrderService';
import { requestAction, setFileListLoading } from '@common/components/common.actions';
import { SwoCviViewModel } from '@models/AuthorizationSwoCvi';
import { InvoicePayeesViewModel } from '@models/InvoicePayees';
import { InvoiceCreateViewModel } from '@models/Invoicing';
import { history } from 'src';
import { RouteDefinitions, getRoute } from '@common/route-definitions';
import { ServiceWorkOrderActionType } from '../service-work-order/service-work-order.actions';
import { FileViewModel, DocumentViewModel } from '@models/Document';
import { documentService } from '@services/DocumentService';

export enum InvoiceActionType {
  SearchInvoiceListStart = 'SearchInvoiceListStart',
  SearchInvoiceListSuccess = 'SearchInvoiceListSuccess',
  SearchInvoiceListFailure = 'SearchInvoiceListFailure',
  GetInvoiceStart = 'GetInvoiceStart',
  GetInvoiceSuccess = 'GetInvoiceSuccess',
  GetInvoiceFailure = 'GetInvoiceFailure',
  OwnershipChangeStart = 'OwnershipChangeStart',
  OwnershipChangeSuccess = 'OwnershipChangeSuccess',
  OwnershipChangeFailure = 'OwnershipChangeFailure',
  SetInvoicableSwoCVIs = 'SetInvoicableSwoCVIs',
  SetInvoicePayees = 'SetInvoicePayees',
  SetInvoicePayeesListLoading = 'SetInvoicePayeesListLoading',
  InvoiceSaving = 'InvoiceSaving',
  FakeSave = 'FakeSave',
  InvoiceReset = 'InvoiceReset',
  SaveInvoiceFilesStart = 'SaveInvoiceFilesStart',
  SaveInvoiceFilesSuccess = 'SaveInvoiceFilesSuccess',
  SaveInvoiceFilesFailure = 'SaveInvoiceFilesFailure',
  RemoveInvoiceFileStart = 'RemoveInvoiceFileStart',
  RemoveInvoiceFileSuccess = 'RemoveInvoiceFileSuccess',
  RemoveInvoiceFileFailure = 'RemoveInvoiceFileFailure',
  GetInvoiceFilesStart = 'GetInvoiceFilesStart',
  GetInvoiceFilesSuccess = 'GetInvoiceFilesSuccess',
  GetInvoiceFilesFailure = 'GetInvoiceFilesFailure',
  CreateInvoiceStart = 'CreateInvoiceStart',
  CreateInvoiceSuccess = 'CreateInvoiceSuccess',
  CreateInvoiceFailure = 'CreateInvoiceFailure'
}

export interface SetInvoiceableSwoCVIsAction extends Action {
  readonly type: InvoiceActionType.SetInvoicableSwoCVIs;
  readonly cvis: Array<SwoCviViewModel>;
}

export interface InvoiceAction extends Action {
  readonly type: InvoiceActionType;
}

/***** Search Invoice List *****/
export interface SearchInvoiceListStartAction extends InvoiceAction {
  readonly query: string | null;
  readonly filterBy: string | null;
}
export interface SearchInvoiceListSuccessAction extends InvoiceAction {
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly result: PageViewModel<any>;
}

export interface SearchInvoiceListFailureAction extends InvoiceAction {
  readonly error: string;
}

export interface GetInvoiceStartAction extends InvoiceAction {}

export interface GetInvoiceSuccessAction extends InvoiceAction {
  readonly invoice: InvoiceViewModel;
}

export interface GetInvoiceFailureAction extends InvoiceAction {
  readonly errorMessage: string;
}

export interface OwnershipChangeStartAction extends InvoiceAction {}

export interface OwnershipChangeSuccessAction extends InvoiceAction {
  readonly id: number;
  readonly user: ProfileViewModel;
  readonly release: boolean | undefined;
}

export interface OwnershipChangeFailureAction extends InvoiceAction {
  readonly error: string;
}

export interface SetInvoicePayeesAction extends InvoiceAction {
  readonly type: InvoiceActionType.SetInvoicePayees;
  readonly invoicePayees: InvoicePayeesViewModel;
}

export interface SetInvoicePayeesListLoadingAction extends InvoiceAction {
  readonly type: InvoiceActionType.SetInvoicePayeesListLoading;
  readonly payeesLoading: boolean;
}

/********** CreateInvoice **********/
export interface CreateInvoiceStartAction extends InvoiceAction {
  readonly type: InvoiceActionType.CreateInvoiceStart;
}
export interface CreateInvoiceSuccessAction extends InvoiceAction {
  readonly type: InvoiceActionType.CreateInvoiceSuccess;
  readonly invoice: InvoiceViewModel;
}
export interface CreateInvoiceFailureAction extends InvoiceAction {
  readonly type: InvoiceActionType.CreateInvoiceFailure;
  readonly errorMessage: string;
}

export interface InvoiceSavingAction extends InvoiceAction {
  readonly type: InvoiceActionType.InvoiceSaving;
  readonly invoiceSaving: boolean;
}

/********** SaveInvoiceFiles **********/
export interface SaveInvoiceFilesStartAction extends InvoiceAction {}
export interface SaveInvoiceFilesSuccessAction extends InvoiceAction {
  readonly invoiceId: number;
  readonly documents: DocumentViewModel[];
}
export interface SaveInvoiceFilesFailureAction extends InvoiceAction {
  readonly errorMessage: string;
}

/********** RemoveInvoiceFile **********/
export interface RemoveInvoiceFileStartAction extends InvoiceAction {}
export interface RemoveInvoiceFileSuccessAction extends InvoiceAction {
  readonly invoiceId: number;
  readonly fileId: number;
}
export interface RemoveInvoiceFileFailureAction extends InvoiceAction {
  readonly errorMessage: string;
}

/********** GetContractFiles **********/
export interface GetInvoiceFilesStartAction extends InvoiceAction {}
export interface GetInvoiceFilesSuccessAction extends InvoiceAction {
  readonly invoiceId: number;
  readonly documents: DocumentViewModel[];
}
export interface GetInvoiceFilesFailureAction extends InvoiceAction {
  readonly errorMessage: string;
}

export interface FakeSave extends InvoiceAction {}

/***** Search Invoice List *****/
export function searchInvoiceList(
  query: string | null,
  pageSize: number,
  pageNumber: number,
  orderBy: string,
  orderDirection: string,
  filterBy: string | null,
  swoDispatchType?: string
) {
  return async (dispatch: Dispatch<any>) => {
    dispatch(searchInvoiceListStart(query, filterBy));
    try {
      let result: PageViewModel<InvoiceSearchViewModel> = await invoiceService.searchInvoiceList(
        query,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterBy,
        swoDispatchType
      );
      dispatch(searchInvoiceListSuccess(pageNumber, pageSize, orderBy, result!));
    } catch (e) {
      dispatch(searchInvoiceListFailure(e.toString()));
    }
  };
}

export function searchInvoiceListStart(query: string | null, filterBy: string | null): SearchInvoiceListStartAction {
  return {
    type: InvoiceActionType.SearchInvoiceListStart,
    query: query,
    filterBy: filterBy
  };
}

export function searchInvoiceListSuccess(
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  result: PageViewModel<any>
): SearchInvoiceListSuccessAction {
  return {
    type: InvoiceActionType.SearchInvoiceListSuccess,
    pageNumber: pageNumber,
    pageSize: pageSize,
    orderBy: orderBy,
    result: result
  };
}

export function searchInvoiceListFailure(error: string): SearchInvoiceListFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error.toString()} />, ToastOptions.Error);

  return {
    type: InvoiceActionType.SearchInvoiceListFailure,
    error: error
  };
}

export function getInvoice(invoiceId: number) {
  return async (dispatch: Dispatch<any>) => {
    dispatch(GetInvoiceStart());
    try {
      const invoice: InvoiceViewModel = await invoiceService.getInvoice(invoiceId);
      return dispatch(GetInvoiceSuccess(invoice));
    } catch (e) {
      return dispatch(GetInvoiceFailure(e.toString()));
    }
  };
}

export function GetInvoiceStart(): GetInvoiceStartAction {
  return {
    type: InvoiceActionType.GetInvoiceStart
  };
}

export function GetInvoiceSuccess(invoice: InvoiceViewModel): GetInvoiceSuccessAction {
  return {
    type: InvoiceActionType.GetInvoiceSuccess,
    invoice: invoice
  };
}

export function GetInvoiceFailure(errorMessage: string): GetInvoiceFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={errorMessage.toString()} />, ToastOptions.Error);
  return {
    type: InvoiceActionType.GetInvoiceFailure,
    errorMessage: errorMessage
  };
}

export function ownershipChange(id: number, user: ProfileViewModel, release?: boolean) {
  return async (dispatch: Dispatch<any>) => {
    dispatch(ownershipChangeStart());
    try {
      await invoiceService.ownershipChange(id, release);
      dispatch(ownershipChangeSuccess(id, user, release));
    } catch (e) {
      dispatch(ownershipChangeFailure(e.toString()));
    }
  };
}

export function ownershipChangeStart(): OwnershipChangeStartAction {
  return {
    type: InvoiceActionType.OwnershipChangeStart
  };
}

export function ownershipChangeSuccess(
  id: number,
  user: ProfileViewModel,
  release?: boolean
): OwnershipChangeSuccessAction {
  return {
    type: InvoiceActionType.OwnershipChangeSuccess,
    id: id,
    user: user,
    release: release
  };
}

export function ownershipChangeFailure(error: string): OwnershipChangeFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error.toString()} />, ToastOptions.Error);
  return {
    type: InvoiceActionType.OwnershipChangeFailure,
    error: error
  };
}

export function setInvoiceableCVIs(cvis: Array<SwoCviViewModel>): SetInvoiceableSwoCVIsAction {
  return {
    type: InvoiceActionType.SetInvoicableSwoCVIs,
    cvis: cvis
  };
}

export function getInvoicableCvis(swoId: number) {
  return (dispatch: AppDispatcher) => {
    return dispatch(requestAction(() => swoService.getSwoCvis(swoId, true), setInvoiceableCVIs, {}));
  };
}

export function getInvoicePayees(swoId: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    if (getState().invoicing.paymentLines.isTableLoading) {
      return;
    }
    dispatch(
      requestAction(() => invoiceService.getInvoicePayees(+swoId), setInvoicePayees, {
        hasToast: false,
        loadingAction: setPayeesListLoading
      })
    );
  };
}

export function setInvoicePayees(payees: InvoicePayeesViewModel) {
  return {
    type: InvoiceActionType.SetInvoicePayees,
    invoicePayees: payees
  };
}

export function setPayeesListLoading(loading: boolean) {
  return {
    type: InvoiceActionType.SetInvoicePayeesListLoading,
    loading: loading
  };
}

export function checkInvoiceCode(vendorId: number, invoiceCode: string) {
  return async () => {
    return await invoiceService.duplicateInvoiceCode(vendorId, invoiceCode);
  };
}

export const createInvoice = (invoice: InvoiceCreateViewModel) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(createInvoiceStart());
    try {
      const newInvoice = await invoiceService.createInvoiceHeader(invoice);
      return await dispatch(createInvoiceSuccess(newInvoice));
    } catch (e) {
      dispatch(createInvoiceFailure(e as string));
    }
    return Promise.reject('');
  };
};

export function createInvoiceStart(): CreateInvoiceStartAction {
  return {
    type: InvoiceActionType.CreateInvoiceStart
  };
}

export function createInvoiceSuccess(invoice: InvoiceViewModel): CreateInvoiceSuccessAction {
  return {
    type: InvoiceActionType.CreateInvoiceSuccess,
    invoice: invoice
  };
}

export function createInvoiceFailure(errorMessage: string): CreateInvoiceFailureAction {
  return {
    type: InvoiceActionType.CreateInvoiceFailure,
    errorMessage: errorMessage
  };
}

export function InvoiceSavingAction(loading: boolean): InvoiceSavingAction {
  return {
    type: InvoiceActionType.InvoiceSaving,
    invoiceSaving: loading
  };
}

/********** GetInvoiceFiles **********/
export function getInvoiceFiles(invoiceId: number) {
  return (dispatch: Dispatch<AppState>) => {
    dispatch(getInvoiceFilesStart());
    documentService
      .getInvoiceFiles(invoiceId)
      .then(documents => {
        dispatch(getInvoiceFilesSuccess(invoiceId, documents));
      })
      .catch(err => {
        dispatch(getInvoiceFilesFailure(err.errorMessage));
      });
  };
}
export function getInvoiceFilesStart(): GetInvoiceFilesStartAction {
  return {
    type: InvoiceActionType.GetInvoiceFilesStart
  };
}
export function getInvoiceFilesSuccess(
  invoiceId: number,
  documents: DocumentViewModel[]
): GetInvoiceFilesSuccessAction {
  return {
    type: InvoiceActionType.GetInvoiceFilesSuccess,
    invoiceId: invoiceId,
    documents: documents
  };
}
export function getInvoiceFilesFailure(errorMessage: string): GetInvoiceFilesFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get invoice files ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: InvoiceActionType.GetInvoiceFilesFailure,
    errorMessage: errorMessage
  };
}

/********** SaveInvoiceFiles **********/
export const saveInvoiceFiles = (invoiceId: number, fileVMs: FileViewModel[]) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(saveInvoiceFilesStart());
    dispatch(setFileListLoading(true));
    try {
      const documentVMs = await documentService.saveFiles(fileVMs, undefined, undefined, undefined, invoiceId);
      dispatch(saveInvoiceFilesSuccess(invoiceId, documentVMs));
      dispatch(getInvoiceFiles(invoiceId));
      dispatch(setFileListLoading(false));
    } catch (e) {
      dispatch(saveInvoiceFilesFailure(e as string));
      dispatch(setFileListLoading(false));
    }
  };
};

export function saveInvoiceFilesStart(): SaveInvoiceFilesStartAction {
  return {
    type: InvoiceActionType.SaveInvoiceFilesStart
  };
}

export function saveInvoiceFilesSuccess(
  invoiceId: number,
  documents: DocumentViewModel[]
): SaveInvoiceFilesSuccessAction {
  return {
    type: InvoiceActionType.SaveInvoiceFilesSuccess,
    invoiceId: invoiceId,
    documents: documents
  };
}

export function saveInvoiceFilesFailure(errorMessage: string): SaveInvoiceFilesFailureAction {
  return {
    type: InvoiceActionType.SaveInvoiceFilesFailure,
    errorMessage: errorMessage
  };
}

/********** RemoveInvoiceFile **********/
export const removeInvoiceFile = (invoiceId: number, fileId: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(removeInvoiceFileStart());
    dispatch(setFileListLoading(true));
    try {
      await documentService.removeFile(fileId);
      dispatch(removeInvoiceFileSuccess(invoiceId, fileId));
      dispatch(getInvoiceFiles(invoiceId));
      dispatch(setFileListLoading(false));
    } catch (e) {
      dispatch(removeInvoiceFileFailure(e as string));
      dispatch(setFileListLoading(false));
    }
  };
};

export function removeInvoiceFileStart(): RemoveInvoiceFileStartAction {
  return {
    type: InvoiceActionType.RemoveInvoiceFileStart
  };
}

export function removeInvoiceFileSuccess(invoiceId: number, fileId: number): RemoveInvoiceFileSuccessAction {
  return {
    type: InvoiceActionType.RemoveInvoiceFileSuccess,
    invoiceId: invoiceId,
    fileId: fileId
  };
}

export function removeInvoiceFileFailure(errorMessage: string): RemoveInvoiceFileFailureAction {
  return {
    type: InvoiceActionType.RemoveInvoiceFileFailure,
    errorMessage: errorMessage
  };
}

// NOTE: THIS IS A FAKE SBMIT FOR INVOICING
export function fakeSaveAndRoute(id: string): InvoiceAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={`Invoice Saved Successfully`} />,
    ToastOptions.Success
  );
  history.push(getRoute(RouteDefinitions.invoicing.Details, { invoiceId: +id }));
  return { type: InvoiceActionType.FakeSave };
}

export function updateInvoice(id: number, invoice: InvoiceCreateViewModel) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    return dispatch(
      requestAction(
        () => invoiceService.updateInvoiceHeader(id, invoice),
        (newInvoice: InvoiceViewModel) => {
          return {
            type: '',
            newInvoice: newInvoice
          };
        },
        {
          hasToast: true,
          toastSuccessMessage: 'Invoice Header updated successfully.',
          overrideFailMessage: 'Unable to update Invoice Header',
          loadingAction: InvoiceSavingAction
        }
      )
    );
  };
}

export function resetInvoiceEdit() {
  return async (dispatch: AppDispatcher) => {
    dispatch({
      type: InvoiceActionType.InvoiceReset
    });
    dispatch({
      type: ServiceWorkOrderActionType.ResetServiceWorkOrder
    });
  };
}



// WEBPACK FOOTER //
// ./src/invoicing/invoicing.actions.tsx